export type WizardLang = "he" | "en"

interface Deliverable {
  icon: string
  title: string
  desc: string
}

interface CustomerTypeOption {
  key: "B2B" | "B2C" | "Mixed"
  label: string
  hint: string
}

interface PillarLabel {
  title: string
}

interface WizardCopy {
  hero: {
    headline: { line1: string; line2: string }
    positioning: { saas: string; agent: string }
    subline: string
    deliverables: Deliverable[]
    trustLine: string
    scrollCue: string
  }
  industries: string[]
  otherKey: string
  otherPlaceholder: string
  customerTypes: CustomerTypeOption[]
  processChips: string[]
  painChipsPrefix: string
  monthlyHoursOptions: string[]
  loadingMessages: string[]
  headings: {
    step1: string
    step2: string
    step3: string
    step3Placeholder: string
    step3MinHint: string
    step3ChipsHint: string
    step4: string
    step5: string
    step5Placeholder: string
    step5OptionalHint: string
  }
  buttons: {
    back: string
    next: string
    skip: string
    submit: string
    finalize: string
  }
  dialog: {
    title: string
    description: string
    bgThinkingLabel: string
  }
  contactFields: {
    name: string
    email: string
    phone: string
  }
  contactErrors: {
    name: string
    email: string
    phone: string
  }
  result: {
    greeting: (firstName: string) => string
    personalizedFor: (industry: string) => string
    painTitle: string
    customerTitle: string
    understood: string
    currentCostTitle: string
    currentCostHoursLabel: (h: number) => string
    currentCostMonthlyLabel: (ils: number) => string
    currentCostRangeNote: string
    solutionTitle: string
    agentsTitle: string
    howItWorksLabel: string
    coreValueLabel: string
    eliminatesLabel: string
    connectsTo: (tool: string) => string
    weeklyHoursSavedLabel: (h: number) => string
    monthlySavingsLabel: (ils: number) => string
    weeksToReadyLabel: (w: number) => string
    totalTimelineLabel: (w: number) => string
    valueTitle: string
    hoursReclaimedLabel: (h: number) => string
    hoursReclaimedSuffix: string
    savings: string
    perYearSuffix: string
    valuePillars: {
      alwaysOn: PillarLabel
      instant: PillarLabel
      brandVoice: PillarLabel
      knowsBusiness: PillarLabel
      lowerCost: PillarLabel
    }
    chartTitle: string
    chartLabelCurrent: string
    chartLabelSavings: string
    charts: {
      costTitle: string
      costToday: string
      costAgent: string
      costCaption: string
      timeTitle: string
      timeHandledLabel: string
      timeBackLabel: (h: number) => string
      timeCaption: string
      hoursTitle: string
      hoursAlwaysOnLabel: string
      hoursStaffedLabel: string
      hoursCaption: string
    }
    successMetricsTitle: string
    successMetricsSubtitle: string
    successMetricTag: string
    roiStripShareLabel: string
    roiStripTimeLabel: string
    nextStepTimeline: {
      step1: { tag: string; title: string; desc: string }
      step2: { tag: string; title: string; desc: (agent: string) => string }
      step3: { tag: string; title: string; desc: string }
    }
    dataNeededTitle: string
    processTitle: string
    whyPalmidosTitle: string
    flow: {
      toCustomer: string
      toProblem: string
      toSolution: string
      toNextStep: string
    }
    nextStep: string
    nextStepEyebrow: string
    bookHere: string
    downloadPdfCta: string
    emailReportCta: string
    emailSending: string
    emailSent: string
    shareDialog: {
      title: string
      alreadySentTo: (email: string) => string
      notYetSent: string
      sendToMeCta: (email: string) => string
      hint: string
      placeholder: string
      sendCta: string
      sending: string
      sentExtra: string
      errorMsg: string
      close: string
    }
    emailedTo: (email: string) => string
    bookCta: string
    chatCta: string
    footnote: string
  }
  impact: {
    high: string
    medium: string
    low: string
  }
  error: string
  retry: string
}

const fmtILS = (n: number) => n.toLocaleString("en-US")

export const COPY: Record<WizardLang, WizardCopy> = {
  he: {
    hero: {
      headline: { line1: "אתם צריכים AI.", line2: "רק לא יודעים מאיפה להתחיל." },
      positioning: {
        saas: "תוכנת SaaS עוזרת לעובד לעשות את העבודה שלו.",
        agent: "סוכן AI מבצע לעובד את העבודה במקומו.",
      },
      subline:
        "ענו על 5 שאלות, ותוך שניות תקבלו דוח חינם שמראה בדיוק אילו סוכני AI כדאי לבנות בחברה שלכם, מה כל אחד יעשה וכמה זמן יחזיר לכם - בלי תשלום ובלי שיחת מכירה.",
      deliverables: [
        {
          icon: "🤖",
          title: "סוכני AI מותאמים אישית",
          desc: "לא תבניות. סוכנים שמכירים את החברה, הנתונים והלקוחות שלכם ומבצעים את העבודה.",
        },
        {
          icon: "⏱️",
          title: "כמה זמן הסוכן יחזיר לכם",
          desc: "תחשיב שקוף: כמה התהליך הזה עולה לכם היום, וכמה ממנו הסוכן ייקח על עצמו.",
        },
        {
          icon: "📄",
          title: "דוח חינם, בלי התחייבות",
          desc: "מה לבנות, איך זה עובד, ומאיפה מתחילים - בלי תשלום ובלי שיחת מכירה.",
        },
      ],
      trustLine: "60 שניות  ·  דוח חינם ומותאם אישית",
      scrollCue: "צרו לי דוח סוכן AI לחברה",
    },
    industries: [
      "הייטק",
      "תוכנה",
      "מסחר",
      "איקומרס",
      "שירותים מקצועיים",
      "תעשייה",
      "לוגיסטיקה",
      "פיננסים",
      "ביטוח",
      "בריאות",
      "רפואה",
      "חינוך",
      "נדל\"ן",
      "מסעדנות",
      "אירוח",
      "שיווק",
      "מדיה",
      "משפט",
      "מלכ\"ר",
      "עמותה",
      "אחר",
    ],
    otherKey: "אחר",
    otherPlaceholder: "כתבו במילים שלכם...",
    customerTypes: [
      { key: "B2B", label: "B2B", hint: "מוכרים לחברות" },
      { key: "B2C", label: "B2C", hint: "מוכרים לצרכן הסופי" },
      { key: "Mixed", label: "שילוב", hint: "גם וגם" },
    ],
    processChips: [
      "מענה לפניות לקוחות",
      "מענה במייל",
      "סינון קו\"ח",
      "תיאום פגישות",
      "הפקת דוחות",
      "סיכום פגישות",
      "ניהול ידע",
      "הצעות מחיר",
      "כספים / חשבונאות",
      "חשבוניות וגביה",
      "אונבורדינג לקוחות",
      "מעקב אחר לידים",
      "ניתוח נתונים",
      "ניהול מלאי",
      "יצירת תוכן",
      "בקרת איכות",
      "ניהול פרויקטים",
      "גיוס / HR",
    ],
    painChipsPrefix: "אזורי כאב נבחרים",
    monthlyHoursOptions: [
      "פחות מ-20 שעות",
      "20-50 שעות",
      "50-100 שעות",
      "100-200 שעות",
      "מעל 200 שעות",
      "לא בטוח",
    ],
    loadingMessages: [
      "קוראים את התשובות שלכם...",
      "מנתחים את הלקוחות והשוק שלכם...",
      "מאבחנים את הכאב הספציפי שלכם...",
      "מתכננים את סוכני ה-AI שמתאימים לכם...",
      "מחשבים כמה זמן הסוכן יחזיר לכם...",
      "כותבים את הדוח האישי שלכם...",
    ],
    headings: {
      step1: "באיזה תחום אתם?",
      step2: "למי אתם מוכרים?",
      step3: "מה לוקח לכם הכי הרבה זמן היום?",
      step3Placeholder:
        "ספציפית - מה התהליך שלכם? מי הלקוח, מה הוא מבקש, איפה הוא נתקע, וכמה זה חוזר על עצמו?",
      step3MinHint: "(לפחות משפט-שניים - ככל שתפרטו יותר, הדוח יהיה חד יותר)",
      step3ChipsHint: "תיוגים נוספים (אופציונלי):",
      step4: "כמה שעות בחודש מבזבזים על זה?",
      step5: "איך זה מתבצע אצלכם היום?",
      step5Placeholder: "מי עושה את זה, באילו כלים, וכמה זמן זה לוקח לכל מקרה?",
      step5OptionalHint: "ככל שתספרו יותר, הדוח יהיה הרבה יותר מדויק וספציפי לכם.",
    },
    buttons: {
      back: "חזרה",
      next: "המשך",
      skip: "דלגו",
      submit: "קבלו את הדוח ←",
      finalize: "אני רוצה לדעת איך לשלב AI לחברה שלי",
    },
    dialog: {
      title: "כמעט שם!",
      description: "השאירו פרטים והדוח שלכם מוכן תוך שניות.",
      bgThinkingLabel: "הסוכן כבר מתחיל לעבוד עליכם",
    },
    contactFields: {
      name: "שם מלא",
      email: "אימייל",
      phone: "טלפון",
    },
    contactErrors: {
      name: "שם נדרש",
      email: "אימייל לא תקין",
      phone: "טלפון נדרש",
    },
    result: {
      greeting: (firstName) => `דוח אישי ל${firstName} 👋`,
      personalizedFor: (industry) => `דוח אישי · ${industry}`,
      painTitle: "הכאב - לעומק",
      customerTitle: "הלקוח שלכם והשוק",
      understood: "מה הבנו",
      currentCostTitle: "כמה שעות זה לוקח לכם היום",
      currentCostHoursLabel: (h) => `${h} שעות עבודה בשבוע`,
      currentCostMonthlyLabel: (ils) => `≈ ₪${fmtILS(ils)} לחודש שהולכים על תהליך שאפשר לעשות אחרת`,
      currentCostRangeNote: "חישבנו לפי אמצע הטווח שבחרתם.",
      solutionTitle: "הפתרון - איך זה עובד",
      agentsTitle: "הסוכנים שתכננו עבורכם",
      howItWorksLabel: "איך זה עובד",
      coreValueLabel: "הערך המרכזי",
      eliminatesLabel: "מבטל:",
      connectsTo: (t) => `מתחבר ל-${t}`,
      weeklyHoursSavedLabel: (h) => `${h} שעות / שבוע`,
      monthlySavingsLabel: (ils) => `₪${fmtILS(ils)} / חודש`,
      weeksToReadyLabel: (w) => `מוכן תוך ${w} שבועות`,
      totalTimelineLabel: () => `כל הסוכנים פעילים עד 60 ימים`,
      valueTitle: "מה תרוויחו מזה",
      hoursReclaimedLabel: (h) => `${h.toLocaleString("en-US")} שעות`,
      hoursReclaimedSuffix: "חוזרות לצוות בשנה",
      savings: "סך הזמן שתחסכו",
      perYearSuffix: " / שנה",
      valuePillars: {
        alwaysOn: { title: "עובד 24/7" },
        instant: { title: "מגיב בשניות" },
        brandVoice: { title: "מדבר יפה ובקול שלכם" },
        knowsBusiness: { title: "מכיר את החברה שלכם" },
        lowerCost: { title: "בערך עשירית מהעלות" },
      },
      chartTitle: "מה אתם מוציאים היום מול מה שהסוכן ייקח על עצמו",
      chartLabelCurrent: "עלות נוכחית",
      chartLabelSavings: "חיסכון שנתי",
      charts: {
        costTitle: "עלות התהליך",
        costToday: "ידנית, היום",
        costAgent: "עם סוכן AI",
        costCaption: "סוכן AI רץ בערך בעשירית מהעלות של לעשות את זה ידנית - ומתרחב עם הנפח בלי לגייס.",
        timeTitle: "כמה מהעבודה עוברת לסוכן",
        timeHandledLabel: "מהעבודה מבוצעת על ידי הסוכן",
        timeBackLabel: (h) => `≈ ${h.toLocaleString("en-US")} שעות חוזרות לצוות בשנה`,
        timeCaption: "הסוכן לוקח על עצמו את הנפח החוזר. אתם נשארים רק עם המקרים שבאמת דורשים שיקול דעת.",
        hoursTitle: "שעות פעילות בשבוע",
        hoursAlwaysOnLabel: "סוכן AI · 24/7",
        hoursStaffedLabel: "צוות אנושי · בקירוב",
        hoursCaption: "הסוכן עובד 168 שעות בשבוע - גם ב-2 בלילה, גם בסופ״ש, גם כשאתם בחופשה.",
      },
      successMetricsTitle: "החזר השקעה ומדדים מדידים על הצלחת הסוכן",
      successMetricsSubtitle: "איך הסוכן יעזור לכם ומה היעדים שלו - כל יעד כאן מדיד וקצוב בזמן, כך שתדעו בשחור-לבן מתי הסוכן מחזיר את ההשקעה.",
      successMetricTag: "יעד",
      roiStripShareLabel: "מהעבודה עוברת לסוכן",
      roiStripTimeLabel: "שעות חוזרות לצוות בשנה",
      nextStepTimeline: {
        step1: { tag: "היום", title: "שיחת היכרות עם החברה לעומק", desc: "נכיר את החברה, התהליכים והלקוחות שלכם לעומק - כדי לאפיין את הסוכן הנכון בדיוק עבורכם" },
        step2: { tag: "תוך שבועיים", title: "פיילוט חי", desc: (a) => `${a} כבר עובד אצלכם בפועל` },
        step3: { tag: "עד 60 יום", title: "כל הסוכנים פעילים", desc: "המערכת המלאה רצה ומחזירה לכם שעות" },
      },
      dataNeededTitle: "מה צריך מכם כדי להתחיל",
      processTitle: "איך מגיעים מכאן לשם",
      whyPalmidosTitle: "למה Palmidos",
      flow: {
        toCustomer: "הלקוח",
        toProblem: "הבעיה",
        toSolution: "הפתרון",
        toNextStep: "הצעד הבא",
      },
      nextStep: "הצעד הבא",
      nextStepEyebrow: "🚀 הצעד הבא",
      bookHere: "קבעו פגישה ישירות כאן ↓",
      downloadPdfCta: "הורידו כ-PDF",
      emailReportCta: "שלחו לי את הדוח למייל",
      emailSending: "שולח...",
      emailSent: "נשלח למייל ✓",
      shareDialog: {
        title: "שיתוף הדוח",
        alreadySentTo: (e) => `הדוח כבר נשלח אליכם למייל ${e}.`,
        notYetSent: "הדוח עדיין לא הגיע אליכם. אפשר לשלוח אותו עכשיו, או להוסיף כתובות נוספות.",
        sendToMeCta: (e) => `שלחו אליי (${e})`,
        hint: "רוצים לשלוח גם לאחרים? הזינו כתובות מייל (אפשר כמה, מופרדות בפסיק).",
        placeholder: "name@company.com, partner@company.com",
        sendCta: "שלחו לכתובות האלו ←",
        sending: "שולח...",
        sentExtra: "נשלח בהצלחה ✓",
        errorMsg: "משהו השתבש. נסו שוב.",
        close: "סגור",
      },
      emailedTo: (e) => `📧 הדוח נשלח גם למייל: ${e}`,
      bookCta: "אני רוצה לבנות סוכן AI",
      chatCta: "דברו איתנו בוואטסאפ",
      footnote: "את הדוח הזה כתב סוכן AI שבנינו. תארו לעצמכם מה ייעודי לחברה שלכם יעשה.",
    },
    impact: {
      high: "השפעה גבוהה",
      medium: "השפעה בינונית",
      low: "השפעה נמוכה",
    },
    error: "משהו השתבש. נסו שוב.",
    retry: "נסה שוב",
  },
  en: {
    hero: {
      headline: { line1: "You need AI.", line2: "You just don't know where to start." },
      positioning: {
        saas: "SaaS software helps your employee do their work.",
        agent: "An AI agent does the work for them.",
      },
      subline:
        "Answer 5 questions and in seconds you'll get a free report showing exactly which AI agents to build in your business, what each one does, and how much time it gives you back - no payment, no sales call.",
      deliverables: [
        {
          icon: "🤖",
          title: "Tailored AI agents",
          desc: "Not templates. Agents that know your business, data, and customers - and do the work.",
        },
        {
          icon: "⏱️",
          title: "How much time it gives back",
          desc: "A transparent calculation: what this costs you today, and how much the agent takes off your plate.",
        },
        {
          icon: "📄",
          title: "Free report, no strings",
          desc: "What to build, how it works, and where to start - no payment, no sales call.",
        },
      ],
      trustLine: "60 seconds  ·  free, personalized to you",
      scrollCue: "Build my AI agent report",
    },
    industries: [
      "Software",
      "Tech",
      "Commerce",
      "E-commerce",
      "Professional services",
      "Industry",
      "Logistics",
      "Finance",
      "Insurance",
      "Healthcare",
      "Education",
      "Real estate",
      "Hospitality",
      "Marketing",
      "Media",
      "Legal",
      "Non-profit",
      "Other",
    ],
    otherKey: "Other",
    otherPlaceholder: "Type your answer...",
    customerTypes: [
      { key: "B2B", label: "B2B", hint: "We sell to businesses" },
      { key: "B2C", label: "B2C", hint: "We sell to end consumers" },
      { key: "Mixed", label: "Mixed", hint: "Both" },
    ],
    processChips: [
      "Customer support",
      "Email triage",
      "Resume screening",
      "Scheduling",
      "Report generation",
      "Meeting summaries",
      "Knowledge management",
      "Quotes & proposals",
      "Bookkeeping",
      "Invoicing & collections",
      "Customer onboarding",
      "Lead follow-up",
      "Data analysis",
      "Inventory management",
      "Content creation",
      "Quality assurance",
      "Project management",
      "HR / Recruiting",
    ],
    painChipsPrefix: "Selected pain areas",
    monthlyHoursOptions: [
      "Less than 20 hours",
      "20-50 hours",
      "50-100 hours",
      "100-200 hours",
      "Over 200 hours",
      "Not sure",
    ],
    loadingMessages: [
      "Reading your answers...",
      "Analyzing your customers and market...",
      "Diagnosing your specific bottleneck...",
      "Designing the AI agents that fit you...",
      "Calculating how much time the agent gives back...",
      "Writing your personalized report...",
    ],
    headings: {
      step1: "What industry are you in?",
      step2: "Who do you sell to?",
      step3: "What's eating most of your time today?",
      step3Placeholder:
        "Specifically - what's YOUR process? Who's the customer, what do they want, where do they get stuck, and how often does this repeat?",
      step3MinHint: "(A sentence or two - the more you describe, the sharper your blueprint will be)",
      step3ChipsHint: "Additional tags (optional):",
      step4: "How many hours per month go to this?",
      step5: "How is it handled today?",
      step5Placeholder: "Who handles it, with which tools, and how long per case?",
      step5OptionalHint: "The more you share, the more precise and specific your blueprint will be.",
    },
    buttons: {
      back: "Back",
      next: "Next",
      skip: "Skip",
      submit: "Get my blueprint →",
      finalize: "Create my blueprint",
    },
    dialog: {
      title: "Almost there!",
      description: "Leave your details and your blueprint will be ready in seconds.",
      bgThinkingLabel: "Our agent is already working on yours",
    },
    contactFields: {
      name: "Full name",
      email: "Email",
      phone: "Phone",
    },
    contactErrors: {
      name: "Name is required",
      email: "Invalid email",
      phone: "Phone is required",
    },
    result: {
      greeting: (firstName) => `A personal report for ${firstName} 👋`,
      personalizedFor: (industry) => `Personalized · ${industry}`,
      painTitle: "The pain - in depth",
      customerTitle: "Your customer & market",
      understood: "What we understood",
      currentCostTitle: "How much time this takes you today",
      currentCostHoursLabel: (h) => `${h} work hours per week`,
      currentCostMonthlyLabel: (ils) => `≈ ₪${fmtILS(ils)} per month going to a process that can be done differently`,
      currentCostRangeNote: "Calculated from the midpoint of your selected range.",
      solutionTitle: "The solution - how it works",
      agentsTitle: "The agents we designed for you",
      howItWorksLabel: "How it works",
      coreValueLabel: "Core value",
      eliminatesLabel: "Eliminates:",
      connectsTo: (t) => `Connects to ${t}`,
      weeklyHoursSavedLabel: (h) => `${h} h / wk`,
      monthlySavingsLabel: (ils) => `₪${fmtILS(ils)} / mo`,
      weeksToReadyLabel: (w) => `Ready in ${w} weeks`,
      totalTimelineLabel: () => `All agents live within 60 days`,
      valueTitle: "What you'll gain",
      hoursReclaimedLabel: (h) => `${h.toLocaleString("en-US")} hours`,
      hoursReclaimedSuffix: "a year back to your team",
      savings: "Total time you'll get back",
      perYearSuffix: " / yr",
      valuePillars: {
        alwaysOn: { title: "Works 24/7" },
        instant: { title: "Replies in seconds" },
        brandVoice: { title: "Speaks well, in your voice" },
        knowsBusiness: { title: "Knows your business" },
        lowerCost: { title: "About a tenth of the cost" },
      },
      chartTitle: "What you spend today vs. what the agent takes off your plate",
      chartLabelCurrent: "Current cost",
      chartLabelSavings: "Annual savings",
      charts: {
        costTitle: "Cost of the work",
        costToday: "By hand, today",
        costAgent: "With an AI agent",
        costCaption: "An AI agent runs at roughly a tenth of the cost of doing it by hand - and scales with volume without hiring.",
        timeTitle: "How much of the work moves to the agent",
        timeHandledLabel: "of the work handled by the agent",
        timeBackLabel: (h) => `≈ ${h.toLocaleString("en-US")} hours a year back to your team`,
        timeCaption: "The agent carries the repetitive volume. You're left only with the cases that genuinely need judgment.",
        hoursTitle: "Working hours per week",
        hoursAlwaysOnLabel: "AI agent · 24/7",
        hoursStaffedLabel: "Human team · approx.",
        hoursCaption: "The agent works 168 hours a week - at 2am, on weekends, and while you're on vacation.",
      },
      successMetricsTitle: "ROI and measurable targets for the agent's success",
      successMetricsSubtitle: "How the agent helps you and what its targets are - every one here is measurable and time-bound, so you'll know in black and white when it's paying for itself.",
      successMetricTag: "Target",
      roiStripShareLabel: "of the work moves to the agent",
      roiStripTimeLabel: "hours a year back to your team",
      nextStepTimeline: {
        step1: { tag: "Today", title: "Deep get-to-know-your-company call", desc: "We get to know your company, processes and customers in depth - to scope the right agent for you" },
        step2: { tag: "In 2 weeks", title: "Live pilot", desc: (a) => `${a} already working in your business` },
        step3: { tag: "Within 60 days", title: "All agents live", desc: "The full system running and giving hours back" },
      },
      dataNeededTitle: "What we'd need from you to start",
      processTitle: "How we get from here to live",
      whyPalmidosTitle: "Why Palmidos",
      flow: {
        toCustomer: "the client",
        toProblem: "the problem",
        toSolution: "the solution",
        toNextStep: "next step",
      },
      nextStep: "Next step",
      nextStepEyebrow: "🚀 Next step",
      bookHere: "Book directly below ↓",
      downloadPdfCta: "Download as PDF",
      emailReportCta: "Email me this report",
      emailSending: "Sending...",
      emailSent: "Emailed ✓",
      shareDialog: {
        title: "Share the report",
        alreadySentTo: (e) => `The report was already emailed to ${e}.`,
        notYetSent: "The report hasn't reached your inbox yet. You can send it now, or add more recipients below.",
        sendToMeCta: (e) => `Send to me (${e})`,
        hint: "Want to share it with others? Add their emails (comma-separated).",
        placeholder: "name@company.com, partner@company.com",
        sendCta: "Send to these addresses →",
        sending: "Sending...",
        sentExtra: "Sent successfully ✓",
        errorMsg: "Something went wrong. Please try again.",
        close: "Close",
      },
      emailedTo: (e) => `📧 The report was emailed to: ${e}`,
      bookCta: "I want to build an AI agent",
      chatCta: "Chat with us on WhatsApp",
      footnote:
        "This blueprint was written by an AI agent we built. Imagine what a dedicated one for your company could do.",
    },
    impact: {
      high: "High impact",
      medium: "Medium impact",
      low: "Low impact",
    },
    error: "Something went wrong. Please try again.",
    retry: "Try again",
  },
}
